import React, { useState, useEffect } from 'react';
import TopBar from '../components/TopBar';
import ChannelCard from '../components/ChannelCard';
import ChannelWizard from '../components/ChannelWizard';
import ChannelDetailModal from '../components/ChannelDetailModal';
import channelService from '../services/channelService';
import { useAuthStore } from '../store/useAuthStore';
import type {
  Channel,
  ChannelStatus,
  OutputFormat,
  ListChannelsParams,
} from '../services/channelService';

const ChannelsPage: React.FC = () => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [deletingAll, setDeletingAll] = useState(false);

  // Modal states
  const [showWizard, setShowWizard] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<ChannelStatus | 'all'>('all');
  const [formatFilter, setFormatFilter] = useState<OutputFormat | 'all'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'status'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 12;

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    running: 0,
    stopped: 0,
    error: 0,
  });

  // Load channels
  const loadChannels = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListChannelsParams = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (formatFilter !== 'all') {
        params.output_format = formatFilter;
      }

      const response = await channelService.listChannels(params);
      setChannels(response.channels);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load channels');
      console.error('Error loading channels:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const data = await channelService.getChannelStats();
      setStats({
        total: parseInt(data.total_channels),
        running: parseInt(data.running_channels),
        stopped: parseInt(data.stopped_channels),
        error: parseInt(data.error_channels),
      });
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  // Initial load
  useEffect(() => {
    loadChannels();
    loadStats();
  }, [page, statusFilter, formatFilter, sortBy, sortOrder]);

  // Handle channel start
  const handleStart = async (channel: Channel): Promise<void> => {
    try {
      await channelService.startChannel(channel.id);
      await loadChannels();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to start channel');
      throw err; // Re-throw so modal knows it failed
    }
  };

  // Handle channel stop
  const handleStop = async (channel: Channel): Promise<void> => {
    try {
      await channelService.stopChannel(channel.id);
      await loadChannels();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to stop channel');
      throw err; // Re-throw so modal knows it failed
    }
  };

  // Handle channel delete
  const handleDelete = async (channel: Channel): Promise<void> => {
    if (!window.confirm(`Are you sure you want to delete channel "${channel.name}"?`)) {
      return;
    }

    try {
      await channelService.deleteChannel(channel.id);
      setShowDetailModal(false);
      setSelectedChannel(null);
      await loadChannels();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete channel');
      throw err; // Re-throw so modal knows it failed
    }
  };

  // Handle channel selection
  const handleSelectChannel = (channel: Channel) => {
    setSelectedChannel(channel);
    setShowDetailModal(true);
  };

  // Handle wizard success
  const handleWizardSuccess = () => {
    loadChannels();
    loadStats();
  };

  // Reset filters
  const resetFilters = () => {
    setStatusFilter('all');
    setFormatFilter('all');
    setSortBy('created_at');
    setSortOrder('desc');
    setPage(1);
  };

  // Delete all channels (admin only)
  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL channels? This action cannot be undone.')) {
      return;
    }
    if (!window.confirm('This will stop all running channels and delete them permanently. Are you absolutely sure?')) {
      return;
    }

    try {
      setDeletingAll(true);
      const result = await channelService.deleteAllChannels();
      alert(`Successfully deleted ${result.deletedCount} channels`);
      loadChannels();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete all channels');
    } finally {
      setDeletingAll(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <TopBar />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Channels</h1>
              <p className="mt-1 text-sm text-gray-600">
                Manage your broadcast channels and live streams
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && stats.total > 0 && (
                <button
                  onClick={handleDeleteAll}
                  disabled={deletingAll}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                >
                  {deletingAll ? (
                    <>
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete All
                    </>
                  )}
                </button>
              )}
              <button
                onClick={() => setShowWizard(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Create Channel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Channels</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Running</p>
                <p className="mt-2 text-3xl font-bold text-green-600">{stats.running}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Stopped</p>
                <p className="mt-2 text-3xl font-bold text-gray-600">{stats.stopped}</p>
              </div>
              <div className="p-3 bg-gray-100 rounded-lg">
                <svg className="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Errors</p>
                <p className="mt-2 text-3xl font-bold text-red-600">{stats.error}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Status filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as any);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="running">Running</option>
                <option value="stopped">Stopped</option>
                <option value="starting">Starting</option>
                <option value="stopping">Stopping</option>
                <option value="error">Error</option>
              </select>
            </div>

            {/* Format filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
              <select
                value={formatFilter}
                onChange={(e) => {
                  setFormatFilter(e.target.value as any);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Formats</option>
                <option value="hls">HLS Only</option>
                <option value="rtmp">RTMP Only</option>
                <option value="both">HLS + RTMP</option>
              </select>
            </div>

            {/* Sort by */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="created_at">Created Date</option>
                <option value="name">Name</option>
                <option value="status">Status</option>
              </select>
            </div>

            {/* Sort order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                {sortOrder === 'asc' ? (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                    Ascending
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Descending
                  </>
                )}
              </button>
            </div>

            {/* Reset filters */}
            <div className="ml-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1 opacity-0">Reset</label>
              <button
                onClick={resetFilters}
                className="px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm transition-colors"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Channel grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p className="text-red-800">{error}</p>
            <button
              onClick={loadChannels}
              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm"
            >
              Retry
            </button>
          </div>
        ) : channels.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <svg
              className="mx-auto w-16 h-16 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No channels found</h3>
            <p className="mt-2 text-sm text-gray-600">
              Get started by creating your first broadcast channel.
            </p>
            <button
              onClick={() => setShowWizard(true)}
              className="mt-6 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
            >
              Create Channel
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {channels.map((channel) => (
                <ChannelCard
                  key={channel.id}
                  channel={channel}
                  onSelect={handleSelectChannel}
                  onStart={handleStart}
                  onStop={handleStop}
                  onDelete={handleDelete}
                  selected={selectedChannel?.id === channel.id}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} channels
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-700">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Channel Wizard Modal */}
      <ChannelWizard
        isOpen={showWizard}
        onClose={() => setShowWizard(false)}
        onSuccess={handleWizardSuccess}
      />

      {/* Channel Detail Modal */}
      <ChannelDetailModal
        channel={selectedChannel}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedChannel(null);
        }}
        onStart={handleStart}
        onStop={handleStop}
        onDelete={handleDelete}
        onRefresh={async () => {
          // Refresh channel data after logo upload
          if (selectedChannel) {
            try {
              const updated = await channelService.getChannel(selectedChannel.id);
              setSelectedChannel(updated);
              loadChannels(); // Also refresh the list
            } catch (err) {
              console.error('Failed to refresh channel:', err);
            }
          }
        }}
      />
    </div>
  );
};

export default ChannelsPage;
